import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';
import { useOutsideClick } from '@src/hooks/core/core-hook-imports.js';
import { DropdownButton } from '@src/components/core/buttons/button-imports.js';
import SearchFilterForm from './search-filter-form.jsx';

/**
 * Renders a dropdown button, which displays radio options for the provided
 * options, when clicked.  The options are searchable when there are eight or
 * more options to show the user.
 */
const SearchFilter = ({
  name,
  queryParameterName,
  disabled,
  hidden,
  options,
  defaultOptionId,
  open,
  clickHandler,
  selectionHandler,
  applyFilterLabel,
  clearLabel,
  allowSearch,
  searchPlaceholder,
  numberOfOptionsBeforeSearch,
}) => {
  const [searchParams] = useSearchParams();
  const [closing, setClosing] = useState(false);

  const selectedOptionId = searchParams.get(queryParameterName);
  const selectedOptionName = selectedOptionId ? options[selectedOptionId]?.label : null;

  const numberOfOptions = Object.keys(options).length;
  const active = open || selectedOptionId;

  useEffect(() => {
    let timeoutId;

    if (closing) {
      // Delay the unmounting of this component so that the closing animation can run
      timeoutId = setTimeout(clickHandler, 200);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [closing]);

  const onClick = () => {
    if (open) {
      setClosing(true);
    }
    else {
      setClosing(false);
      clickHandler();
    }
  };

  const ref = useOutsideClick(onClick);

  return (
    <div ref={open ? ref : null} className='search-filter-dropdown--wrapper'>
      <DropdownButton
        className={active && 'active'}
        disabled={disabled || numberOfOptions == 0}
        hidden={hidden}
        open={open}
        clickHandler={onClick}
      >
        <div className='filter-text'>{selectedOptionName ?? name}</div>
      </DropdownButton>
      {
        open &&
        <SearchFilterForm
          name={name}
          queryParameterName={queryParameterName}
          options={options}
          defaultOptionId={defaultOptionId}
          selectedOptionId={selectedOptionId}
          selectionHandler={selectionHandler}
          applyFilterLabel={applyFilterLabel}
          clearLabel={clearLabel}
          allowSearch={allowSearch}
          searchPlaceholder={searchPlaceholder}
          numberOfOptionsBeforeSearch={numberOfOptionsBeforeSearch}
          closeHandler={onClick}
          closing={closing}
        />
      }
    </div>
  );
}

SearchFilter.propTypes = {
  /**
   * the name to display on the filter, by default
   */
  name: PropTypes.string.isRequired,
  /**
   * the query parameter whose value should change with the filter's value
   */
  queryParameterName: PropTypes.string.isRequired,
  /**
   * whether or not the dropdown should be visually disabled and unable to be clicked
   */
  disabled: PropTypes.bool,
  /**
   * controls whether dropdown is visible or hidden, allowing for conditionally visible filters
   */
  hidden: PropTypes.bool,
  /**
   * the options to display to the user for selection
   *
   * @example
   * {
   *  'primary-care': {
   *    label: 'Primary Care',
   *    disabled: false,
   *  },
   *  'oncology': {
   *    label: 'Oncology',
   *    disabled: true,
   *  },
   *  'behavioral-health': {
   *    label: 'Behavioral Health',
   *    disabled: false,
   *  },
   * }
   */
  options: PropTypes.object.isRequired,
  /**
   * the option to default to when no selection is made, if any is desired
   */
  defaultOptionId: PropTypes.string,
  /**
   * whether or not the filter options should be shown for user selection
   */
  open: PropTypes.bool.isRequired,
  /**
   * the callback function for when the filter is clicked
   */
  clickHandler: PropTypes.func.isRequired,
  /**
   * the callback function for when the a selection is applied
   */
  selectionHandler: PropTypes.func,
  /**
   * the text to display for the button to apply a filter change
   */
  applyFilterLabel: PropTypes.string.isRequired,
  /**
   * the text to display to clear all filter options
   */
  clearLabel: PropTypes.string.isRequired,
  /**
   * whether or not to show a search box for filtering options if there are more
   * options than the specified numberOfOptionsBeforeSearch (required if allowSearch is true)
   */
  allowSearch: PropTypes.bool,
  /**
   * the text to display in the search box, by default, when there are enough
   * options to show a search box to filter them
   */
  searchPlaceholder: PropTypes.string,
  /**
   * the number of options to display before a search box is displayed that
   * allows the user to filter the available options
   */
  numberOfOptionsBeforeSearch: PropTypes.number,
};

SearchFilter.defaultProps = {
  disabled: false,
  allowSearch: true,
  numberOfOptionsBeforeSearch: 8,
};

export default SearchFilter;
